import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  InputGroup,
  Badge,
  ButtonGroup,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  CONVERSATION_STATUS,
  HANDOFF_REASONS,
} from '../../config/aiConversationStatus';
import callOutcomes from '../../config/callOutcomes';

const ADVANCE_CHOICES = [10, 30, 45];

const SimulatorChat = ({
  messages,
  status,
  cost,
  turnsLeft,
  loading,
  onSend,
  onAdvance,
  onDiscard,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const bottom = useRef(null);

  useEffect(() => {
    if (bottom.current) bottom.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const closed = !!(status && status.closed);

  // The engine's words are not labels for a person: the same maps the lead
  // page uses turn them into something a colleague can judge. Falling back
  // to the raw value keeps a new engine state visible instead of blank.
  const statusLabel = CONVERSATION_STATUS[(status || {}).conversationStatus];
  const outcomeLabel = callOutcomes[(status || {}).outcome];
  const reasonLabel = HANDOFF_REASONS[(status || {}).handoffReason];

  const send = () => {
    const trimmed = text.trim();
    if (!trimmed || loading || closed) return;
    setText('');
    onSend(trimmed);
  };

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <Badge
            color={statusLabel ? statusLabel.color : 'light'}
            className="mr-2"
          >
            {statusLabel
              ? t(statusLabel.title)
              : (status && status.conversationStatus) || '—'}
          </Badge>
          {status && status.outcome && (
            <Badge
              color={outcomeLabel ? outcomeLabel.color : 'info'}
              className="mr-2"
            >
              {outcomeLabel && (
                <i className={`${outcomeLabel.icon} align-middle mr-1`}></i>
              )}
              {outcomeLabel ? t(outcomeLabel.title) : status.outcome}
            </Badge>
          )}
          {status && status.handoffReason && (
            <Badge color="warning">
              {reasonLabel ? t(reasonLabel) : status.handoffReason}
            </Badge>
          )}
        </div>
        <div className="text-muted font-size-12">
          {t('Turns left')}: {turnsLeft === null ? '—' : turnsLeft} ·{' '}
          {t('Cost')}: ${(cost.sessionUsd || 0).toFixed(4)}
        </div>
      </CardHeader>

      <CardBody style={{ height: '52vh', overflowY: 'auto' }}>
        {messages.map((message, index) => (
          <div
            key={`${message.atMs}-${index}`}
            className={`d-flex mb-2 ${
              message.role === 'user' ? 'justify-content-end' : ''
            }`}
          >
            <div
              className={`p-2 px-3 rounded ${
                message.role === 'user' ? 'bg-primary text-white' : 'bg-light'
              }`}
              style={{ maxWidth: '75%', whiteSpace: 'pre-wrap' }}
            >
              {message.content}
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </CardBody>

      <CardBody className="border-top">
        <InputGroup className="mb-2">
          <Input
            value={text}
            disabled={loading || closed}
            placeholder={
              closed
                ? t('The conversation is over')
                : t('Answer as the lead would')
            }
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button color="primary" onClick={send} disabled={loading || closed}>
            {loading ? t('Waiting…') : t('Send')}
          </Button>
        </InputGroup>

        <div className="d-flex justify-content-between align-items-center">
          <div>
            <span className="text-muted font-size-12 mr-2">
              {t('Let time pass')}:
            </span>
            <ButtonGroup size="sm">
              {ADVANCE_CHOICES.map((minutes) => (
                <Button
                  key={minutes}
                  color="light"
                  disabled={loading || closed}
                  onClick={() => onAdvance(minutes)}
                >
                  +{minutes} {t('min')}
                </Button>
              ))}
            </ButtonGroup>
          </div>
          {/* Deliberately not disabled while loading/closed: a colleague
              stuck on a slow turn must be able to get out. The reducer
              ignores a turn that lands after this, so the escape is safe. */}
          <Button color="link" className="text-danger" onClick={onDiscard}>
            {t('Start over')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default SimulatorChat;
